import { MarketingHeader } from '../../components/marketing/MarketingHeader';
import { MarketingFooter } from '../../components/marketing/MarketingFooter';
import { SEOHead } from '../../components/SEO/SEOHead';
import { StructuredData } from '../../components/SEO/StructuredData';
import { createBreadcrumbListSchema } from '../../components/SEO/StructuredData';

export const TermsPage = () => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://simcarlo.com';

  return (
    <div className="marketing-page">
      <SEOHead
        metadata={{
          title: 'Terms of Service - Usage Agreement',
          description: 'Read Simcarlo\'s Terms of Service to understand the rules and guidelines for using our platform. Learn about your rights and responsibilities.',
          keywords: ['terms of service', 'terms and conditions', 'user agreement', 'legal', 'terms'],
          ogImage: `${baseUrl}/images/Landing Main.png`,
          canonicalUrl: `${baseUrl}/terms`,
        }}
      />
      <StructuredData
        schemas={[
          createBreadcrumbListSchema([
            { name: 'Home', url: baseUrl },
            { name: 'Terms of Service', url: `${baseUrl}/terms` },
          ]),
        ]}
      />
      <MarketingHeader />

      {/* Hero Section */}
      <section className="marketing-hero">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h1 className="marketing-hero-title" style={{ textAlign: 'left', marginBottom: '16px' }}>Terms of Service</h1>
            <p className="marketing-hero-subtitle" style={{ textAlign: 'left', marginBottom: '24px' }}>
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="marketing-hero-description" style={{ textAlign: 'left' }}>
              These terms govern your use of Simcarlo. Please read them carefully.
            </p>
          </div>
        </div>
      </section>

      {/* Agreement Section */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>Agreement to Terms</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  These Terms of Service ("Terms") govern your access to and use of Simcarlo ("Service"). By using the Service,
                  you agree to be bound by these Terms and our Privacy Policy. If you do not agree, do not use the Service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility and License Section */}
      <section className="marketing-solution">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Eligibility</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  You must be at least 13 years old (or the age of digital consent in your jurisdiction) to use the Service.
                  If you use the Service on behalf of an organization, you represent that you have authority to bind that organization.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Use License</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '16px' }}>
                  Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to use the Service.
                  This license does not include and you agree not to:
                </p>
                <ul className="marketing-feature-list" style={{ textAlign: 'left' }}>
                  <li><span className="marketing-checkmark">✓</span> Modify, copy, distribute, or create derivative works of the Service</li>
                  <li><span className="marketing-checkmark">✓</span> Reverse engineer, decompile, or attempt to extract source code</li>
                  <li><span className="marketing-checkmark">✓</span> Use the Service for unlawful, harmful, or fraudulent purposes</li>
                  <li><span className="marketing-checkmark">✓</span> Remove or alter any copyright, trademark, or proprietary notices</li>
                  <li><span className="marketing-checkmark">✓</span> Access the Service to build a competing product or service</li>
                </ul>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Accounts and Security</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
                  Notify us immediately if you suspect unauthorized use. We may suspend or terminate accounts that violate these Terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Section */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>Subscription and Billing</h2>
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Paid Plans</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  If you subscribe to a paid plan, you agree to pay the fees specified at checkout. Subscriptions are billed in advance
                  on a monthly or annual basis via our payment processor (Stripe). Fees may be subject to taxes and are non-refundable except as required by law.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Cancellation</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  You may cancel your subscription at any time in your account or by contacting support. Cancellation takes effect at the end of the current billing period.
                  You will continue to have access to paid features until that date.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Refunds</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We offer a free trial for paid plans (where applicable). Charges made after the trial are generally non-refundable.
                  We may, at our sole discretion, provide refunds or credits in specific circumstances.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* User Content Section */}
      <section className="marketing-solution">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>User Content and Data</h2>
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Content Ownership</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  You retain ownership of the simulations, models, and content you create ("User Content"). You grant us a limited,
                  worldwide, non-exclusive, revocable license to host, store, process, and display User Content solely to provide,
                  maintain, and improve the Service. We do not claim ownership of your models or results.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>AI Features</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  If you use AI-assisted features, you authorize us to process prompts and related context with our AI providers to return results.
                  You are responsible for reviewing outputs; AI outputs may be inaccurate or incomplete and should not be relied upon without verification.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prohibited Uses Section */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>Prohibited Uses</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '24px' }}>
                  You may not use Simcarlo:
                </p>
                <ul className="marketing-feature-list" style={{ textAlign: 'left', maxWidth: '100%', margin: '0' }}>
                  <li><span className="marketing-checkmark">✓</span> In any way that violates applicable laws or regulations</li>
                  <li><span className="marketing-checkmark">✓</span> To transmit any malicious code or viruses</li>
                  <li><span className="marketing-checkmark">✓</span> To attempt to gain unauthorized access to the Service or other accounts</li>
                  <li><span className="marketing-checkmark">✓</span> To interfere with or disrupt the Service's integrity or performance</li>
                  <li><span className="marketing-checkmark">✓</span> To abuse, harass, or harm other users or third parties</li>
                  <li><span className="marketing-checkmark">✓</span> To upload or process content without necessary rights or permissions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Terms Section */}
      <section className="marketing-solution">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Service Availability</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We strive for high availability but do not guarantee uninterrupted or error-free Service.
                  The Service may be unavailable due to maintenance, updates, or events beyond our control.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Warranties Disclaimer</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  The Service is provided "as is" and "as available" without warranties of any kind, express or implied, including
                  warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service
                  will be uninterrupted, error-free, secure, or free of harmful components.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Limitation of Liability</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  To the maximum extent permitted by law, Simcarlo and its affiliates will not be liable for any indirect, incidental, special,
                  consequential, or punitive damages, or any loss of profits, revenues, data, or use, arising out of or related to your use of the Service,
                  even if advised of the possibility of such damages.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Indemnification</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  You agree to defend, indemnify, and hold harmless Simcarlo and its affiliates, officers, employees, and agents
                  from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of
                  or in any way connected with your User Content or your use of the Service in violation of these Terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Terms Section */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Intellectual Property</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We own all rights, title, and interest in and to the Service, including all software, materials, and trademarks, except for your User Content.
                  No rights are granted except as expressly set forth in these Terms.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Third-Party Services</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  The Service may integrate with third-party services (e.g., payments, hosting, analytics). Your use of third-party services is governed
                  by their terms and policies, and we are not responsible for their actions or omissions.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Termination</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We may suspend or terminate your access to the Service at any time for any reason, including violations of these Terms.
                  Upon termination, the license granted to you will cease. Certain sections will survive termination, including
                  Intellectual Property, Indemnification, Limitation of Liability, and Governing Law.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Governing Law; Dispute Resolution</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  These Terms are governed by the laws of your local jurisdiction unless otherwise required. Any disputes shall be resolved in courts of competent jurisdiction
                  or through agreed alternative dispute resolution mechanisms. Where permitted, you and Simcarlo waive the right to a jury trial.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Changes to the Service and Terms</h3>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  We may modify the Service and these Terms from time to time. We will notify you of material changes (e.g., by email or in-app notice).
                  Continued use of the Service after changes become effective constitutes acceptance of the updated Terms.
                </p>
              </div>

              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '22px', marginBottom: '16px', color: 'var(--text-primary)' }}>Miscellaneous</h3>
                <ul style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', paddingLeft: '24px' }}>
                  <li>Severability: If any provision is unenforceable, the remaining terms remain in effect.</li>
                  <li>Waiver: Failure to enforce a provision is not a waiver of the right to do so later.</li>
                  <li>Assignment: You may not assign these Terms without our consent; we may assign them as part of a merger or sale.</li>
                  <li>Entire Agreement: These Terms and the Privacy Policy form the entire agreement between you and Simcarlo.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="marketing-solution">
        <div className="marketing-container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="marketing-section-title" style={{ textAlign: 'left' }}>Contact Information</h2>
            
            <div className="marketing-features-grid" style={{ gridTemplateColumns: '1fr', gap: '32px', marginTop: '40px' }}>
              <div className="marketing-feature-card" style={{ textAlign: 'left' }}>
                <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  If you have questions about these Terms, contact us at{' '}
                  <a href="mailto:legal@simcarlo.com" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>legal@simcarlo.com</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};
